import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Buildings, ForkKnife, TreeStructure, Gear, SignOut, 
  ArrowRight, Users, ChartBar, CalendarCheck, GraduationCap, Package
} from "@phosphor-icons/react";

const APPS = [
  {
    id: "operating-map",
    title: "CME Operating Map",
    description: "Interactive organization workflow system with roles, departments, processes, and systems",
    icon: TreeStructure,
    color: "bg-primary",
    path: "/dashboard",
    features: ["Org Chart", "Process Flows", "KPIs", "Training Tours"],
  },
  {
    id: "canteen",
    title: "Canteen Management",
    description: "Track daily meal counts for employees and guests with QR badge check-in",
    icon: ForkKnife,
    color: "bg-orange-500",
    path: "/canteen",
    features: ["QR Check-in", "Meal Counts", "Reports", "Employee Tracking"],
  },
  {
    id: "training",
    title: "Training Intelligence System",
    description: "AI-powered training, AI-generated quizzes, assessments, certificates and Zoho People sync",
    icon: GraduationCap,
    color: "bg-emerald-600",
    path: "/training",
    features: ["AI Quiz", "Auto Grading", "Certificates", "Zoho Sync"],
  },
  {
    id: "inventory",
    title: "Inventory Management",
    description: "Procure & track admin, housekeeping, IT and general supplies with items, vendors, POs and issuance",
    icon: Package,
    color: "bg-amber-600",
    path: "/inventory",
    features: ["Item Master", "Vendors", "PO / MR / GRN", "Low-Stock Alerts"],
  },
];

export default function AppSelector() {
  const navigate = useNavigate();
  const { user, logout, isAdmin, canAccessMap, canAccessCanteen, canAccessTraining, canAccessAdmin, canAccessInventory } = useAuth();

  // Filter app tiles by the user's module access
  const visibleApps = APPS.filter((a) => {
    if (a.id === "operating-map") return canAccessMap;
    if (a.id === "canteen") return canAccessCanteen;
    if (a.id === "training") return canAccessTraining;
    if (a.id === "inventory") return canAccessInventory;
    return false;
  });

  // If user has access to only one module, auto-redirect them to it
  useEffect(() => {
    if (!user) return;
    if (visibleApps.length === 1) {
      navigate(visibleApps[0].path, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, visibleApps.length]);

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="app-selector">
      {/* Header */}
      <header className="glass-header border-b border-black/10 sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 md:px-6 h-16 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-md">
              <Buildings size={24} weight="bold" className="text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground tracking-tight">CME PeopleHub</h1>
              <p className="text-xs text-muted-foreground">CME Envirosystems</p>
            </div>
          </div>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center gap-2" data-testid="user-menu-btn">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">
                    {user?.is_super_admin ? "Super Admin" : user?.role}
                  </Badge>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span>{user?.name}</span>
                  <span className="text-xs font-normal text-muted-foreground">{user?.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {canAccessAdmin && (
                <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="admin-panel-link">
                  <Gear size={16} className="mr-2" />
                  Admin Panel
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="logout-btn">
                <SignOut size={16} className="mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4 md:p-8 max-w-7xl mx-auto">
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-2">Welcome back, {user?.name?.split(" ")[0]}!</h2>
          <p className="text-muted-foreground">Select an application to get started</p>
        </div>

        {/* Apps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {visibleApps.map((app) => {
            const Icon = app.icon;
            return (
              <Card
                key={app.id}
                className="group cursor-pointer hover:shadow-lg transition-all hover:border-primary/30"
                onClick={() => navigate(app.path)}
                data-testid={`app-card-${app.id}`}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className={`w-14 h-14 ${app.color} rounded-lg flex items-center justify-center shadow-lg`}>
                      <Icon size={28} weight="bold" className="text-white" />
                    </div>
                    <ArrowRight 
                      size={20} 
                      className="text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" 
                    />
                  </div>
                  <CardTitle className="mt-4">{app.title}</CardTitle>
                  <CardDescription>{app.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {app.features.map((feature, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {feature}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Stats — only for users with map access (the stats are about the org map) */}
        {canAccessMap && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold mb-4">Quick Overview</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">11</p>
                  <p className="text-xs text-muted-foreground">Roles Defined</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Buildings size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">9</p>
                  <p className="text-xs text-muted-foreground">Departments</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                  <ChartBar size={20} className="text-violet-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">4</p>
                  <p className="text-xs text-muted-foreground">Processes</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <CalendarCheck size={20} className="text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">Today</p>
                  <p className="text-xs text-muted-foreground">{new Date().toLocaleDateString()}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t mt-12 py-6">
        <div className="text-center text-sm text-muted-foreground">
          CME Envirosystems Pvt. Ltd. &copy; {new Date().getFullYear()} • PeopleHub
        </div>
      </footer>
    </div>
  );
}
